import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Form, Input, Typography, Space, Divider, Row, Col, notification, Alert } from 'antd'
import { GoogleOutlined, LinkedinOutlined } from '@ant-design/icons'

import QueryString from 'query-string'
import cryptoRandomString from 'crypto-random-string'

import SpinLoader from '../../../helpers/SpinLoader'

import { useAuthContext } from '../../../../context/auth'
import { IntegrationTypes, TLoginRequest } from '../../../../context/types'
import { useGoogle } from '../../../helpers/google'

import { linkedinClientId, linkedinLoginRedirectUri } from '../../../../config'

import './index.less'
import { customLocalStorage } from '../../../../context/customStorage'
import { useIntegrationContext } from '../../../../context/integration'

const { Title, Paragraph } = Typography

type TLoginProps = {
	onGoToRegister?: () => void
	skipRedirect?: boolean
	redirectUrl?: string
	redirectOnLogin?: string
}

function LoginForm({ onGoToRegister, skipRedirect, redirectUrl, redirectOnLogin }: TLoginProps) {
	const { error, code, error_description } = QueryString.parse(window.location.search)
	const { integration } = useIntegrationContext()

	const [form] = Form.useForm()
	const [resetPassword, setResetPassword] = useState(false)
	const [formErrors, setFormErrors] = useState([])

	const { auth, actions } = useAuthContext()

	const history = useHistory()

	const googleAuth = useGoogle(
		(tokenId: string) => actions.googleLogin(tokenId, skipRedirect, redirectUrl),
		actions.setError
	)

	useEffect(() => {
		actions.setError()
		if (code) {
			actions.linkedinLogin(code as string, skipRedirect, redirectUrl)
			history.replace({ search: '' })
		}
		if (redirectUrl) {
			customLocalStorage.setItem('redirectUrl', redirectUrl)
		}
	}, [])

	const onValuesChange = () => {
		actions.setError()
	}

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	const onFinish = (values: TLoginRequest) => {
		actions.login(values, skipRedirect, redirectUrl)
	}

	if (error) {
		actions.setError((error_description as string) || 'Login failed! Please try again.')
	}

	const newState = cryptoRandomString({ length: 10, type: 'base64' })
	const linkedinUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedinClientId}&redirect_uri=${linkedinLoginRedirectUri}&scope=r_emailaddress,r_liteprofile&state=${newState}`

	const goToRegister = onGoToRegister ? onGoToRegister : () => history.push('/register')

	const handleResetPassword = () => {
		const email = form.getFieldValue('email')

		if (email)
			actions.resetPassword(email).finally(() => {
				setResetPassword(false)
				notification.success({
					message: `Check your email for a link to reset your password. If it doesn’t appear within a few minutes, check your Spam folder.`,
				})
			})
	}

	return (
		<SpinLoader loading={!!auth.loading}>
			<Form
				name="LoginForm"
				form={form}
				onFinish={onFinish}
				scrollToFirstError
				layout="vertical"
				className="register-form login-form"
				onValuesChange={onValuesChange}
				onFieldsChange={onFieldsChange}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					<div className="title-container">
						<Title level={2}>Login</Title>
						{(!integration.type || integration.type === IntegrationTypes.top_level) && (
							<div className="social-register">
								<span>or login with</span>
								<Button
									type="primary"
									shape="circle"
									block
									icon={<GoogleOutlined />}
									onClick={googleAuth}
									loading={!!auth.loading}
								/>
								<Button
									shape="circle"
									type="primary"
									block
									icon={<LinkedinOutlined />}
									href={linkedinUrl}
									onClick={() => {
										if (redirectOnLogin) {
											customLocalStorage.setItem('redirectUrl', redirectOnLogin)
										}
									}}
									loading={!!auth.loading}
								/>
							</div>
						)}
					</div>
				</Space>
				<Form.Item
					name="email"
					label="Email"
					hasFeedback
					rules={[
						{
							type: 'email',
							message: 'The input is not valid E-mail!',
						},
						{
							required: true,
							message: 'Please input your E-mail!',
						},
					]}
				>
					<Input />
				</Form.Item>
				{!resetPassword && (
					<Form.Item
						name="password"
						label="Password"
						rules={[
							{
								required: true,
								message: 'Please input your password!',
							},
						]}
						hasFeedback
					>
						<Input.Password />
					</Form.Item>
				)}
				{!resetPassword && (
					<>
						<Row>
							<Col flex="none">
								<Form.Item>
									<Button shape="round" type="primary" htmlType="submit" loading={!!auth.loading}>
										Log In
									</Button>
								</Form.Item>
							</Col>
							<Col flex="none">
								<Form.Item>
									<Button shape="round" type="link" onClick={goToRegister} loading={!!auth.loading}>
										<b>Register</b>
									</Button>
								</Form.Item>
							</Col>
						</Row>
						{auth.error && (
							<Alert showIcon message="Error" description={auth.error} type="error" style={{ marginBottom: 20 }} />
						)}
						{formErrors.length > 0 && (
							<Alert
								showIcon
								type="warning"
								message="Warning"
								description={formErrors.map((v) => (
									<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
										{v.errors[0]}
									</Paragraph>
								))}
							/>
						)}
						<Divider />
						<Paragraph style={{ textAlign: 'center', margin: 0 }}>
							<Button type="link" onClick={() => setResetPassword(true)}>
								Forgotten password?
							</Button>
						</Paragraph>
					</>
				)}
				{!!resetPassword && (
					<Row>
						<Col>
							<Button shape="round" type="primary" loading={!!auth.loading} onClick={handleResetPassword}>
								Reset password
							</Button>
						</Col>
						<Col>
							<Button type="link" onClick={() => setResetPassword(false)}>
								Cancel
							</Button>
						</Col>
					</Row>
				)}
				{!!resetPassword && auth.error && (
					<Alert showIcon message="Error" description={auth.error} type="error" style={{ marginTop: 20 }} />
				)}
				{!!resetPassword && formErrors.length > 0 && (
					<Alert
						showIcon
						type="warning"
						message="Warning"
						style={{ marginTop: 20 }}
						description={formErrors.map((v) => (
							<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
								{v.errors[0]}
							</Paragraph>
						))}
					/>
				)}
			</Form>
		</SpinLoader>
	)
}

export default LoginForm
